import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
} from '../../../services/settings/appStyle/global.style';
import { Loader, Table, IconButton as IconButtonWithText } from '../../../Components/uielements';
import { IntlMessages } from '../../../helper/langHelper';
import { AddPlusIcon, EditIcon, EyeIcon } from '../../../Components/icons';
import { UserContainer } from '../UserV2/user.style';
import { LoadingContent, LoadingModal } from '../EmailTemplate/emailTemplate.style';
import { getCheckList, getCheckListForListing } from '../../../services/bioplus-services/checkList';
import moment from 'moment';
import CreateCheckListModal from './modals/createCheckListModal';
import ViewCheckListModal from './modals/viewCheckListModal';
import { useUserContext } from '../../../hooks/UserContext';
import AssignCheckListToUser from './modals/assignCheckListToUser';
import ViewCheckListAssignedUsers from './modals/viewCheckListAssignedUsers';
import CheckListViewResponse from './modals/checkListViewResponse';
import { useCompany } from '../../../hooks/CompanyContext';

// Maps the acting user's role to the list of checklist `userType`s they
// are NOT allowed to edit.
// - OWNER / EMPLOYEE: cannot edit checklists created by ADMIN or PARTNER (Distributor).
// - PARTNER (Distributor): cannot edit checklists created by ADMIN.
const NOT_ALLOW_EDIT_CHECKLIST_MAP = {
  OWNER: ['ADMIN', 'PARTNER'],
  EMPLOYEE: ['ADMIN', 'PARTNER'],
  PARTNER: ['ADMIN'],
};

const CheckList = () => {
  const [editCheckListId, setEditCheckListId] = useState(null);
  const [viewCheckListId, setViewCheckListId] = useState(null);
  const [isCreateCheckList, setIsCreateCheckList] = useState(false);
  const [addUserToCheckList, setAddUserToCheckList] = useState(null);
  const [viewAssignedUsers, setViewAssignedUsers] = useState(null);
  const [viewResponse, setViewResponse] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;

  const queryClient = useQueryClient();
  const userContext = useUserContext();
  const companyId = companyFilter ?? userContext?.company?.companyId?._id;
  const userType = companyFilter
    ? 'OWNER'
    : userContext?.company?.role === 'EMPLOYEE'
    ? 'OWNER'
    : userContext?.company?.role || 'ADMIN';

  // Role based permission check for the "premise" module
  const { userPermission } = userContext;
  const isCheckListAllowToManage = userPermission?.find(
    (permission) => permission?.label === 'checklist'
  );

  const canCreateCheckList = !!isCheckListAllowToManage?.isCreate;
  const canManageCheckList = !!isCheckListAllowToManage?.isManage;

  // Admin/Partner only get the "add user / view assigned users / view response"
  // action group when a company is selected. Every other role just follows
  // canManageCheckList like before.
  const userRole = userContext?.company?.role;
  const isAdminOrPartner = userRole === 'ADMIN' || userRole === 'PARTNER';
  const canShowManageActions = isAdminOrPartner
    ? companyFilter != null && canManageCheckList
    : canManageCheckList;

  // This is used only for the default role. If the default role is EMPLOYEE, send OWNER instead.
  const checkListData = useQuery(['checkLists', page, pageSize, companyId, userType], () =>
    getCheckListForListing(page, pageSize, {
      // userType: userContext?.company?.role || 'ADMIN',
      userType,
      listType: 'CHECKLIST',
      companyId,
    })
  );
  const checkListTableData = checkListData?.data?.checklists ?? [];

  const activeCheckListId = editCheckListId || viewCheckListId;
  const checkListQuery = useQuery(
    ['checkList', activeCheckListId],
    () => getCheckList({ questionnaireId: activeCheckListId }),
    {
      enabled: !!activeCheckListId,
      onError: (error) => {
        alert(`Error: ${error?.response?.data?.message || 'Failed to load checklist'}`);
        setEditCheckListId(null);
        setViewCheckListId(null);
      },
    }
  );

  const handleEditCheckList = (data) => {
    queryClient.removeQueries(['checkList', data._id]);
    setViewCheckListId(null); // Close view if open
    setEditCheckListId(data._id);
  };

  const handleViewCheckList = (checkListId) => {
    queryClient.removeQueries(['checkList', checkListId]);
    setEditCheckListId(null); // Close edit if open
    setViewCheckListId(checkListId);
  };

  const handleCloseEditModal = () => {
    setEditCheckListId(null);
  };

  const handleCloseViewModal = () => {
    setViewCheckListId(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateCheckList(false);
  };

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };

  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const groupColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.checkList.title" defaultMessage="Check List Title" />,
        accessor: 'title',
        width: canManageCheckList ? '25%' : '40%',
      },

      {
        Header: <IntlMessages id="table.col.createdAt" defaultMessage="Created At" />,
        accessor: 'createdAt',
        width: canManageCheckList ? '25%' : '40%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },

      {
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: canManageCheckList ? '50%' : '20%',
      },
    ],
    [canManageCheckList]
  );

  // Whether the current user is blocked from editing a given checklist,
  // based on their role and the checklist's userType.
  const isEditCheckListDisabled = (data) => {
    const blockedUserTypes = NOT_ALLOW_EDIT_CHECKLIST_MAP[userContext?.company?.role] || [];
    return blockedUserTypes.includes(data?.userType);
  };

  const actionList = [
    ...(canManageCheckList
      ? [
          (data) => (
            <IconButton
              onClick={() => handleEditCheckList(data)}
              disabled={isEditCheckListDisabled(data)}
              style={
                isEditCheckListDisabled(data) ? { opacity: 0.4, cursor: 'not-allowed' } : undefined
              }
            >
              <EditIcon style={{ width: '22px', height: '22px' }} />
            </IconButton>
          ),
        ]
      : []),
    ...(canShowManageActions
      ? [
          (data) => (
            <IconButton onClick={() => setAddUserToCheckList(data)}>
              <IconButtonWithText
                text={<IntlMessages id="button.text.add.user" />}
                icon={<AddPlusIcon />}
              />
            </IconButton>
          ),
          (data) => (
            <IconButton onClick={() => setViewAssignedUsers(data)}>
              <IconButtonWithText
                text={<IntlMessages id="button.text.view.assigned.users" />}
                icon={<EyeIcon style={{ width: '22px', height: '22px' }} />}
              />
            </IconButton>
          ),
          (data) => (
            <IconButton onClick={() => setViewResponse(data)}>
              <IconButtonWithText
                text={<IntlMessages id="button.text.view.response" />}
                icon={<EyeIcon style={{ width: '22px', height: '22px' }} />}
              />
            </IconButton>
          ),
        ]
      : []),
    (data) => (
      <IconButton onClick={() => handleViewCheckList(data?._id)}>
        <EyeIcon style={{ width: '22px', height: '22px' }} />
      </IconButton>
    ),
  ];

  if (checkListData.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <StyledContainer>
        <UserContainer>
          <HeaderTitle>
            <IntlMessages id="module.check.list" />
          </HeaderTitle>

          {canCreateCheckList && (
            <IconButtonWithText
              text={<IntlMessages id="create.check.list" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setIsCreateCheckList(true);
              }}
            />
          )}
        </UserContainer>
        <TableWrapper>
          <Table
            data={checkListTableData}
            columns={groupColumns}
            actions={actionList}
            hasFooter={true}
            page={page - 1}
            pageSize={pageSize}
            prevPage={handlePreviousPage}
            nextPage={handleNextPage}
            total={checkListData?.data?.checklists?.length || 0}
          />
        </TableWrapper>
      </StyledContainer>
      {checkListQuery.isLoading && activeCheckListId && (
        <LoadingModal>
          <LoadingContent>
            <Loader loadingText="Loading checklist data..." />
          </LoadingContent>
        </LoadingModal>
      )}
      {/* Modals */}
      {isCreateCheckList && <CreateCheckListModal onClose={handleCloseCreateModal} />}
      {editCheckListId && checkListQuery?.data && !checkListQuery.isLoading && (
        <CreateCheckListModal
          onClose={handleCloseEditModal}
          data={checkListQuery?.data?.checklist}
          actionEditOrCreate="edit"
          checkListId={editCheckListId}
        />
      )}
      {viewCheckListId && checkListQuery?.data && !checkListQuery.isLoading && (
        <ViewCheckListModal data={checkListQuery?.data?.checklist} onClose={handleCloseViewModal} />
      )}
      {addUserToCheckList && (
        <AssignCheckListToUser
          onClose={() => setAddUserToCheckList(null)}
          checkListId={addUserToCheckList?._id}
          checkListTitle={addUserToCheckList?.title || ''}
          companyId={companyId}
        />
      )}
      {viewAssignedUsers && (
        <ViewCheckListAssignedUsers
          onClose={() => setViewAssignedUsers(null)}
          checkListId={viewAssignedUsers?._id}
          checkListTitle={viewAssignedUsers?.title || ''}
          companyId={companyId}
        />
      )}
      {viewResponse && (
        <CheckListViewResponse
          onClose={() => setViewResponse(null)}
          questionnaireId={viewResponse?._id}
          companyId={companyId}
        />
      )}
    </>
  );
};

export default CheckList;
